// ═══════════════════════════════════════════════════════════════════════════
// Shared helpers for the AI → EditorCommand translator
// ═══════════════════════════════════════════════════════════════════════════
//
// Kept as a tiny module so each of the specialised translator files
// (basic, dynamic, attribute, lifecycle) can import without pulling in
// the full catalogue.

import type { EditorCommand, LayoutContext } from "@dxf-ai-editor/contracts";
import type { CommandDescriptor } from "@dxf-ai-editor/command-registry";

/**
 * Source tag applied to every command emitted by the AI dispatcher. The
 * `"system"` source matches the `commandSourceSchema` member we reserve
 * for non-human-driven command origins (AI, automation, tests).
 */
export const AI_COMMAND_SOURCE = "system" as const;

/**
 * Internal HINT property (NOT a contract field) the INSERT_BLOCK translator
 * stamps on the trailing `LAST_INSERT_ID_SENTINEL` `update-attribute-values`
 * command: the number of `geometry/create-block-insert` commands emitted for
 * THIS INSERT_BLOCK (rowCount × columnCount). The safe-contour dispatch boundary
 * (`aiSafeContour.ts`) uses it to bind the attribute update to the FIRST insert
 * of THIS block's group — `created[created.length - groupSize]` — so an
 * attr-free INSERT_BLOCK immediately followed by an attributed one no longer
 * leaks the FIRST block's insert as the attribute target (#283-1). The boundary
 * STRIPS this hint before dispatch, so it never reaches the reducer / contract.
 */
export const AI_INSERT_GROUP_SIZE_HINT = "__aiInsertGroupSize" as const;

/**
 * Advance an {@link AiDispatchContext} by the side effects of a just-translated
 * batch slice, so a LATER view+space command in the SAME AI batch resolves
 * against the space a PRECEDING SET_LAYOUT / MSPACE / PSPACE already activated —
 * NOT the frozen initial context.
 *
 * The safe contour translates the WHOLE batch before dispatch, so every
 * translator otherwise sees only the initial `context`. Without this a batch
 * `[SET_LAYOUT layout-2, SWITCH_TO_MODEL_SPACE]` resolves MSPACE from the stale
 * initial active space (the FIRST layout), jumping away from the layout-2 the
 * batch just set (#290-16). The ONLY batch-visible state a view+space command
 * mutates is the active space + drawing context, BOTH carried by the canonical
 * `shell/activate-space` command — so we fold the LAST emitted activate-space's
 * `spaceId` into `activeSpaceId` AND its `layoutContext` into the running
 * `layoutContext` (#292 P2): a batch `[SWITCH_TO_MODEL_SPACE, CREATE_LINE]`
 * threads "viewport" forward so the line routes to MODEL space via
 * `pickSpaceId`, while `[SET_LAYOUT/PSPACE, CREATE_LINE]` threads "paper" so
 * the line lands on the just-activated layout. The fold mirrors the shell
 * reducer's resolution (shellUiReducer `shell/activate-space`): a "model"
 * target → "model"; otherwise the command's explicit `layoutContext` wins (the
 * AI view+space translators always set it), with the reducer's "model"→"paper"
 * promotion when absent. Returns the SAME reference when nothing changed.
 */
export function advanceDispatchContext(
  context: AiDispatchContext,
  emitted: readonly EditorCommand[],
): AiDispatchContext {
  let activeSpaceId = context.activeSpaceId;
  let layoutContext = context.layoutContext;
  for (const command of emitted) {
    if (command.type === "shell/activate-space") {
      activeSpaceId = command.spaceId;
      layoutContext =
        command.spaceId === "model"
          ? "model"
          : (command.layoutContext ??
            (layoutContext === "model" ? "paper" : layoutContext));
    }
  }
  if (
    activeSpaceId === context.activeSpaceId &&
    layoutContext === context.layoutContext
  ) {
    return context;
  }
  return { ...context, activeSpaceId, layoutContext };
}

/**
 * Sentinel insert-id emitted by `translateInsertBlock` on the trailing
 * `geometry/update-attribute-values` command of an INSERT_BLOCK-with-attributes
 * proposal. At translation time the concrete id of the INSERT-to-be-created is
 * not yet known, so the translator parks this placeholder; the dispatch
 * boundary (the app safe contour, `aiSafeContour.ts`) resolves it to the id of
 * the INSERT it just created and substitutes it BEFORE dispatching the
 * attribute update. A reducer that receives the unsubstituted sentinel is a
 * graceful no-op (no INSERT has this id), so an integrator that cannot resolve
 * it must DROP the command rather than silently drop the attribute values.
 */
export const LAST_INSERT_ID_SENTINEL = "__last__" as const;

/**
 * Context the dispatcher needs to resolve AI commands that reference the
 * active document by name rather than by explicit ids. All fields are
 * optional because many AI commands are fully self-contained.
 */
export interface AiDispatchContext {
  /** Active space id (defaults to "model"). */
  activeSpaceId?: string;
  /**
   * The shell's drawing-context mode — mirrors the `layoutContext` field the
   * canonical `shell/activate-space` command / shell reducer maintain
   * alongside `activeSpaceId` (#292 P2). "viewport" = floating model space
   * through an active viewport (MSPACE_VIA_VP): there the shell keeps
   * `activeSpaceId` AT the hosting LAYOUT id — MSPACE / PSPACE layout
   * resolution (`resolveActivationLayoutId`) depends on that and must NOT see
   * "model" — while geometry creates default to "model" instead (the VP edits
   * MODEL geometry through the layout; AutoCAD floating-model-space parity).
   * `pickSpaceId` applies that routing; `advanceDispatchContext` threads the
   * mode through a batch. Optional: when omitted the active space is used
   * verbatim (self-contained / legacy contexts).
   */
  layoutContext?: LayoutContext;
  /**
   * Ordered list of the document's PAPER-SPACE LAYOUT space ids (DrawingSpace
   * with `kind === "layout"`; excludes "model", block-edit / refedit sessions),
   * in tab order. The single source of truth the VIEW+SPACE translators use to:
   *   • MSPACE / PSPACE — resolve the layout to activate (the active layout when
   *     already on one, else the FIRST layout — mirroring the CLI MSPACE
   *     model→layout-1 move) so `activeSpaceId` is mutated CONSISTENTLY with
   *     `layoutContext`. Empty / unresolvable → the translator fails CLOSED.
   *   • MVIEW (CREATE_VIEWPORT) — validate the supplied `spaceId` is a real
   *     paper-space layout (reject "model" / orphan ids — MVIEW is layout-only).
   * Optional: when omitted (e.g. dispatch-spy tests that don't exercise layout
   * resolution) the MVIEW translator-level check is skipped (the reducer remains
   * the fail-closed safety net); MSPACE / PSPACE then fail closed (no layout to
   * resolve).
   */
  paperSpaceLayoutIds?: readonly string[];
  /**
   * Optional resolver that maps a parameter name → parameter id for a
   * given INSERT. The dispatcher uses it to convert
   * `UPDATE_DYNAMIC_PARAMETER` / `SET_VISIBILITY_STATE` /
   * `LOOKUP_SELECT` commands (which carry friendly names) into the
   * id-based internal commands. When the resolver is absent or returns
   * null the dispatcher emits a best-effort fallback that the reducer
   * rejects gracefully — this mirrors other AI-facing failure modes
   * (invalid block name, etc).
   */
  resolveParameterIdByName?: (args: {
    insertId: string;
    parameterName: string;
  }) => string | null;
  /**
   * Optional resolver for visibility state name → state id. Same
   * semantics as `resolveParameterIdByName`.
   */
  resolveVisibilityStateIdByName?: (args: {
    insertId: string;
    parameterName: string;
    stateName: string;
  }) => string | null;
  /**
   * Optional resolver for lookup output → parameter updates. Given a
   * lookup table output value, returns the sequence of parameter
   * updates that apply. When absent we emit a diagnostic-only command
   * the reducer treats as a no-op.
   */
  resolveLookupOutput?: (args: {
    insertId: string;
    parameterName: string;
    outputValue: string | number;
  }) => Array<{
    parameterName: string;
    value: number | string | boolean;
  }> | null;
  /**
   * The LIVE MIRRTEXT system variable (0 | 1), projected from the session
   * sysvar store (`useSysvarStore`) by the shell. The MIRROR translator stamps
   * it onto the emitted `geometry/mirror-entities` command WHEN the AI proposal
   * omits its own `mirrtext` — so an AI MIRROR honours a live
   * `SETVAR MIRRTEXT 1` instead of the hard schema default (Codex #342 F2).
   * Preserves the AI safe contour: the AI PROPOSES (optional field), the
   * executor (translator) COERCES the live sysvar. Mirrors the draft/CLI path
   * (`useDraftSession` stamps `useSysvarStore.getState().values.MIRRTEXT`).
   * Optional: when omitted (self-contained / legacy / test dispatch) the
   * translator falls back to the AutoCAD default 0.
   */
  mirrtext?: number;
}

/**
 * Back-compat / footgun normaliser for a domain dispatcher whose signature
 * gained a `context` 2nd argument AFTER a legacy `(command, descriptors)` form.
 *
 * A legacy / untyped (JS) caller still passing a descriptor ARRAY as the SECOND
 * argument (an alternate / restricted registry — e.g. a test disabling a
 * command) would otherwise have that array silently treated as `context`, while
 * `descriptors` falls back to `ALL_COMMAND_DESCRIPTORS` — re-enabling the very
 * command the caller tried to disable, breaking the CC-2a fail-closed guard.
 * `AiDispatchContext` is always a plain object and never an array, so an array
 * second arg can only be the legacy `descriptors`: detect it and re-route.
 *
 * ONE shared truth for every dispatcher that needs it (view+space, draw-modify);
 * each calls this as the first line of its translator so the legacy array path
 * still routes to `descriptors` and fails closed (Codex #287-13, #342 F5).
 */
export function normalizeDispatcherArgs(
  context: AiDispatchContext,
  descriptors: readonly CommandDescriptor[],
): { context: AiDispatchContext; descriptors: readonly CommandDescriptor[] } {
  if (Array.isArray(context)) {
    return { context: {}, descriptors: context };
  }
  return { context, descriptors };
}

/**
 * Compute the internal spaceId for an AI command, falling back to the
 * active space when the AI did not pick one. Kept as a helper to avoid
 * the "?? 'model'" repetition.
 *
 * In the "viewport" layout context (MSPACE_VIA_VP — floating model space
 * through an active VP) the fallback is "model", NOT the active space: the
 * shell keeps `activeSpaceId` at the hosting LAYOUT id there (MSPACE/PSPACE
 * layout resolution needs it), but geometry created in that context lands in
 * MODEL space (AutoCAD floating-model-space parity, #292 P2). An explicit
 * `command.spaceId` always wins, and the final `?? "model"` default still
 * applies when `activeSpaceId` is undefined.
 */
export function pickSpaceId(
  command: { spaceId?: string },
  context: AiDispatchContext,
): string {
  return (
    command.spaceId ??
    (context.layoutContext === "viewport" ? "model" : context.activeSpaceId) ??
    "model"
  );
}

/**
 * Convert degrees → radians. The internal geometry/create-block-insert
 * command expects radians (matching the DXF storage). All AI-exposed
 * rotation values are in degrees (matches Properties palette display).
 */
export function degreesToRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}
